import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { SequenceBuilder } from './sequence-builder'
import { useUpdateSequence } from './use-campaigns'
import { moveGlobalSamplesToEveryStep, legacyGlobalSamples } from './sequence-steps'
import type { SequenceConfig, SequenceStep } from './types'

type EditSequenceDialogProps = {
  readonly campaignId: string
  readonly currentSequence: SequenceConfig
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

export function EditSequenceDialog({
  campaignId,
  currentSequence,
  open,
  onOpenChange,
}: EditSequenceDialogProps) {
  const [steps, setSteps] = useState<readonly SequenceStep[]>(
    currentSequence.steps,
  )
  // "One set for all steps" was retired 2026-10-28 (plan N4). A campaign
  // saved with it keeps sending with those samples until the user moves
  // them onto each step; until then the per-step boxes stay hidden,
  // because the send would ignore them.
  const [globalSamples, setGlobalSamples] = useState<readonly string[]>(() => legacyGlobalSamples(currentSequence))

  const updateSequence = useUpdateSequence()

  // The dialog stays mounted between opens: start each open from the saved
  // sequence, so a Move (or any edit) that was cancelled is not carried over.
  useEffect(() => {
    if (!open) return
    setSteps(currentSequence.steps)
    setGlobalSamples(legacyGlobalSamples(currentSequence))
  }, [open, currentSequence])

  function moveToEveryStep() {
    setSteps((current) => moveGlobalSamplesToEveryStep(current, globalSamples))
    setGlobalSamples([])
  }

  function handleSave() {
    const updatedSequence: SequenceConfig = {
      ...currentSequence,
      steps,
      // Legacy samples not moved yet: save exactly as before. Otherwise
      // per step (a 'global' campaign without samples behaves the same).
      style_samples_mode: globalSamples.length > 0 ? 'global' : 'per_step',
      style_samples_global: globalSamples,
    }

    updateSequence.mutate(
      { campaignId, sequence: updatedSequence },
      {
        onSuccess: () => onOpenChange(false),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Outreach Sequence</DialogTitle>
        </DialogHeader>

        <div className="py-4 max-h-[60vh] overflow-y-auto flex flex-col gap-4">
          {globalSamples.length > 0 && (
            <div className="rounded-lg border bg-card p-3 flex flex-col gap-2">
              <p className="text-sm font-semibold">Writing samples used for every step</p>
              <p className="text-xs text-muted-foreground">
                This campaign uses one set of samples for all of its messages. Writing samples are now set per
                step. These keep working as they are until you move them.
              </p>
              <ul className="flex flex-col gap-1.5">
                {globalSamples.map((sample, i) => (
                  <li key={i} className="whitespace-pre-wrap rounded-md bg-muted/40 px-3 py-2 text-xs">
                    {sample}
                  </li>
                ))}
              </ul>
              <Button type="button" variant="outline" size="sm" className="self-start" onClick={moveToEveryStep}>
                Move these onto each step
              </Button>
              <p className="text-[11px] text-muted-foreground">
                Moving them copies them onto every step, so each message keeps using them. You can then edit
                them step by step. Nothing changes until you save.
              </p>
            </div>
          )}
          <SequenceBuilder steps={steps} onChange={setSteps} showSamples={globalSamples.length === 0} />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateSequence.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateSequence.isPending || steps.length === 0}
          >
            {updateSequence.isPending ? 'Saving...' : 'Save Sequence'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
